/**
 * Edge Sync Dashboard
 * Monitors edge site connectivity, sync status, and recent transactions.
 *
 * ST-00228 (audit TRK-9): the page reads the signed-in endpoints /api/edge/sites…
 * (server/routes/edgeSites.ts, edge_sync.view). It used to call the edge machine API,
 * which only accepts a site's X-API-Key, so it could never load for a person. Converted
 * from index.js (CLAUDE.md TypeScript rule).
 */

import { useState, useEffect, useCallback, type ComponentType, type CSSProperties } from 'react';
import {
  Radio,
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Server,
  Truck,
  ChevronDown,
  ChevronUp,
  Info,
  type LucideIcon,
} from 'lucide-react';
import type { AxiosError } from 'axios';
import api from '../../../services/api';
import usePolling from '../../../hooks/usePolling';
import LexiBridgeLoader from '../../common/LexiBridgeLoader';
import PageHeaderJs from '../../common/PageHeader';
// Types only: the facade module itself also loads the map, which this page does not need.
import type { PageHeaderProps } from '../../../types/commonComponents';
import type {
  EdgeSite,
  EdgeSitesResponse,
  EdgeSyncLog,
  EdgeSyncLogResponse,
  EdgeTransaction,
  EdgeTransactionsResponse,
} from '../../../types/clientPages/edgeSync';

const PageHeader = PageHeaderJs as unknown as ComponentType<PageHeaderProps>;

const mono: CSSProperties = { fontFamily: 'IBM Plex Mono, monospace' };
const body: CSSProperties = { fontFamily: 'DM Sans, sans-serif' };

const SWITCHED_OFF_MESSAGE =
  'Edge sync is switched off on this server. Registered sites are listed, but they cannot pull or push until EDGE_SYNC_ENABLED is set.';

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface HeartbeatState {
  label: string;
  color: 'slate' | 'emerald' | 'amber' | 'red';
  icon: LucideIcon;
}

function heartbeatStatus(lastHeartbeat: string | null): HeartbeatState {
  if (!lastHeartbeat) return { label: 'No data', color: 'slate', icon: XCircle };
  const mins = (Date.now() - new Date(lastHeartbeat).getTime()) / 60000;
  if (mins < 5) return { label: 'Online', color: 'emerald', icon: CheckCircle2 };
  if (mins < 15) return { label: 'Stale', color: 'amber', icon: AlertTriangle };
  return { label: 'Offline', color: 'red', icon: XCircle };
}

const DIRECTION_STYLES: Record<'pull' | 'push', { bg: string; text: string; icon: LucideIcon }> = {
  pull: { bg: 'bg-blue-50', text: 'text-blue-700', icon: ArrowDownToLine },
  push: { bg: 'bg-purple-50', text: 'text-purple-700', icon: ArrowUpFromLine },
};

const STATUS_STYLES: Record<string, string> = {
  success: 'bg-emerald-50 text-emerald-700',
  partial: 'bg-amber-50 text-amber-700',
  failed: 'bg-red-50 text-red-700',
};

const statusOf = (err: unknown): number | undefined =>
  (err as AxiosError | undefined)?.response?.status;

const SyncDashboard = () => {
  const [sites, setSites] = useState<EdgeSite[]>([]);
  const [edgeSyncEnabled, setEdgeSyncEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSite, setExpandedSite] = useState<string | null>(null);
  const [siteLogs, setSiteLogs] = useState<Record<string, EdgeSyncLog[]>>({});
  const [siteTransactions, setSiteTransactions] = useState<Record<string, EdgeTransaction[]>>({});
  const [siteErrors, setSiteErrors] = useState<Record<string, string>>({});
  const [refreshing, setRefreshing] = useState(false);

  const loadSites = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await api.get<EdgeSitesResponse>('/edge/sites');
      setSites(res.data?.sites || []);
      setEdgeSyncEnabled(res.data?.edgeSyncEnabled !== false);
    } catch (err) {
      if (statusOf(err) === 403) {
        setError('You do not have permission to view Edge Sync.');
      } else {
        setError('Failed to load edge sites. Please try again.');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Paused while this tab is in the background; refreshes on return.
  usePolling(
    useCallback(() => loadSites(true), [loadSites]),
    30000
  );

  const toggleSite = async (siteId: string) => {
    if (expandedSite === siteId) {
      setExpandedSite(null);
      return;
    }
    setExpandedSite(siteId);
    setSiteErrors((prev) => {
      const next = { ...prev };
      delete next[siteId];
      return next;
    });

    // Load logs and transactions for this site
    const id = encodeURIComponent(siteId);
    try {
      const [logsRes, txnRes] = await Promise.all([
        api.get<EdgeSyncLogResponse>(`/edge/sites/${id}/log?limit=20`),
        api.get<EdgeTransactionsResponse>(`/edge/sites/${id}/transactions?limit=10`),
      ]);
      setSiteLogs((prev) => ({ ...prev, [siteId]: logsRes.data?.logs || [] }));
      setSiteTransactions((prev) => ({ ...prev, [siteId]: txnRes.data?.transactions || [] }));
    } catch (err) {
      console.error('Failed to load site details:', err);
      setSiteErrors((prev) => ({
        ...prev,
        [siteId]:
          statusOf(err) === 404
            ? 'This site is no longer available.'
            : 'Could not load the sync activity and transactions for this site.',
      }));
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadSites(true);
  };

  if (loading) {
    return (
      <div
        style={{ ...body, backgroundColor: 'var(--app-bg)' }}
        className="flex items-center justify-center min-h-[60vh]"
      >
        <LexiBridgeLoader message="Loading sync status" />
      </div>
    );
  }

  return (
    <div style={body} className="min-h-screen bg-slate-50">
      <PageHeader
        icon={Radio}
        title="Edge Sync"
        subtitle="Monitor edge site connectivity and data sync"
        actions={
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh"
            title="Refresh"
            className="inline-flex items-center justify-center gap-2 h-10 min-w-10 px-2.5 border border-slate-300 bg-surface text-slate-700 hover:border-slate-400 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} aria-hidden="true" />
            <span className="page-header__label">Refresh</span>
          </button>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {/* Error state */}
        {error && (
          <div role="alert" className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium text-amber-800">{error}</p>
            </div>
          </div>
        )}

        {/* Edge sync switched off on this server */}
        {!error && !edgeSyncEnabled && (
          <div
            role="status"
            className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-6 flex items-start gap-3"
          >
            <Info size={20} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">{SWITCHED_OFF_MESSAGE}</p>
          </div>
        )}

        {/* No sites */}
        {!error && sites.length === 0 && (
          <div className="bg-surface rounded-xl border border-slate-200 p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
              <Server size={28} className="text-slate-300" />
            </div>
            <h3 className="text-base font-semibold text-slate-800 mb-2">
              No Edge Sites Registered
            </h3>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Register an edge site in the Edge_Sites table with an API key hash to begin syncing.
            </p>
          </div>
        )}

        {/* Site cards */}
        <div className="space-y-4">
          {sites.map((site) => {
            const hb = heartbeatStatus(site.Last_Heartbeat_At);
            const HBIcon = hb.icon;
            const isExpanded = expandedSite === site.Site_ID;
            const logs = siteLogs[site.Site_ID] || [];
            const txns = siteTransactions[site.Site_ID] || [];
            const detailError = siteErrors[site.Site_ID];

            return (
              <div
                key={site.Site_ID}
                className="bg-surface rounded-xl border border-slate-200 overflow-hidden"
              >
                {/* Site header */}
                <button
                  type="button"
                  onClick={() => toggleSite(site.Site_ID)}
                  aria-expanded={isExpanded}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${hb.color}-50`}
                    >
                      <HBIcon size={20} className={`text-${hb.color}-500`} />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-800">{site.Site_Name}</h3>
                        <span
                          className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-medium rounded"
                          style={mono}
                        >
                          {site.Site_Code}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold bg-${hb.color}-50 text-${hb.color}-700`}
                        >
                          {hb.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5" style={mono}>
                        {site.Tailscale_IP || 'No IP'} &middot; {site.Site_ID}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        Last Heartbeat
                      </p>
                      <p className="text-xs text-slate-600 font-medium" style={mono}>
                        {timeAgo(site.Last_Heartbeat_At)}
                      </p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        Last Pull
                      </p>
                      <p className="text-xs text-slate-600 font-medium" style={mono}>
                        {timeAgo(site.Last_Sync_Pull_At)}
                      </p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        Last Push
                      </p>
                      <p className="text-xs text-slate-600 font-medium" style={mono}>
                        {timeAgo(site.Last_Sync_Push_At)}
                      </p>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={16} className="text-slate-400" />
                    ) : (
                      <ChevronDown size={16} className="text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="border-t border-slate-100">
                    {detailError && (
                      <div
                        role="alert"
                        className="flex items-center gap-2 px-5 py-3 bg-red-50 text-red-700 text-xs border-b border-red-100"
                      >
                        <AlertTriangle size={14} className="flex-shrink-0" />
                        {detailError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-px bg-slate-100">
                      {/* Sync Log */}
                      <div className="bg-surface p-5">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Activity size={13} />
                          Recent Sync Activity
                        </h4>
                        {logs.length === 0 ? (
                          <p className="text-xs text-slate-400">No sync activity yet</p>
                        ) : (
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {logs.map((log) => {
                              const dir =
                                DIRECTION_STYLES[log.Sync_Direction as 'pull' | 'push'] ||
                                DIRECTION_STYLES.pull;
                              const DirIcon = dir.icon;
                              return (
                                <div key={log.Log_ID} className="flex items-center gap-3 py-1.5">
                                  <div
                                    className={`w-6 h-6 rounded flex items-center justify-center ${dir.bg}`}
                                  >
                                    <DirIcon size={12} className={dir.text} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-medium text-slate-700">
                                        {log.Entity_Type}
                                      </span>
                                      <span
                                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[log.Status] || 'bg-slate-50 text-slate-600'}`}
                                      >
                                        {log.Status}
                                      </span>
                                      <span className="text-[10px] text-slate-400" style={mono}>
                                        {log.Records_Count} records
                                      </span>
                                    </div>
                                  </div>
                                  <span
                                    className="text-[10px] text-slate-400 whitespace-nowrap"
                                    style={mono}
                                  >
                                    {log.Duration_MS != null ? `${log.Duration_MS}ms` : ''} &middot;{' '}
                                    {timeAgo(log.Created_At)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Recent Transactions */}
                      <div className="bg-surface p-5">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <Truck size={13} />
                          Recent Edge Transactions
                        </h4>
                        {txns.length === 0 ? (
                          <p className="text-xs text-slate-400">No transactions synced yet</p>
                        ) : (
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {txns.map((txn) => (
                              <div
                                key={txn.Edge_Transaction_ID}
                                className="flex items-center gap-3 py-1.5 border-b border-slate-50 last:border-0"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-bold text-slate-800" style={mono}>
                                      {txn.Plate_Number}
                                    </span>
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                        txn.Status === 'completed'
                                          ? 'bg-emerald-50 text-emerald-700'
                                          : txn.Status === 'initiated'
                                            ? 'bg-blue-50 text-blue-700'
                                            : 'bg-slate-50 text-slate-600'
                                      }`}
                                    >
                                      {txn.Status}
                                    </span>
                                    {txn.Direction && (
                                      <span className="text-[10px] text-slate-400">
                                        {txn.Direction}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    {txn.Nett_Weight_KG != null && (
                                      <span className="text-[10px] text-slate-500" style={mono}>
                                        {Number(txn.Nett_Weight_KG).toLocaleString()} kg nett
                                      </span>
                                    )}
                                    {txn.Order_No && (
                                      <span className="text-[10px] text-slate-400">
                                        Order: {txn.Order_No}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <span
                                  className="text-[10px] text-slate-400 whitespace-nowrap"
                                  style={mono}
                                >
                                  {timeAgo(txn.Synced_At)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SyncDashboard;
